import AddIcon from "@mui/icons-material/Add";
import { Box, Container, Dialog, Fab } from "@mui/material";
import Alert from "@mui/material/Alert";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useListTitle from "hooks/useListTitle";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  BaseFilter,
  IResourceLibrary,
  ListRequestOfBaseFilter,
  ListResponseOfResourceLibrary,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { SnackbarMessageProps } from "types/common-types";
import ResourceLibraryCreateForm from "./ResourceLibraryCreateForm";

interface IResourceLibraryWithId extends IResourceLibrary {
  id: string;
}

const ResourceLibrariesList = () => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const {
    resourceLibraryService,
    resourceLibrariesDetailsUrl,
  } = useGlobalContext();
  const { organizationId } = contextStore.useState();

  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [createOpen, createOpenActions] = useBoolean(false);
  const queryClient = useQueryClient();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  useListTitle("menubarResourceLibraries");

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfResourceLibrary>(
    ["resourceLibraries", organizationId, page, rowsPerPage, orderBy, order],
    () =>
      resourceLibraryService.getResourceLibraries(
        ListRequestOfBaseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: BaseFilter.fromJS({}),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const resourceLibraries = data?.data as IResourceLibraryWithId[];

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 1,
      type: "text",
      label: t({ id: "resourceLibraryListNameLabel" }),
    },
    {
      accessor: "organizationName",
      customSortAccessor: "organization.name",
      align: "left",
      width: 1,
      type: "text",
      label: t({ id: "organization" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(resourceLibrariesDetailsUrl(id));
  };

  const onResourceLibraryCreated = () => {
    queryClient.invalidateQueries(["resourceLibraries"]);
    createOpenActions.setFalse();

    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "resourceLibraryCreated" }))
    );
  };

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Fab
          aria-label="add"
          color="primary"
          onClick={createOpenActions.setTrue}
          data-cy="addButton"
        >
          <AddIcon />
        </Fab>
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: resourceLibraries ?? [],
          loading: isFetching,

          dense: true,
          title: t({ id: "menubarResourceLibraries" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) =>
              resourceLibrariesDetailsUrl(id)
            ),
          ],
        }}
      />
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <ResourceLibraryCreateForm
          onResourceLibraryCreated={onResourceLibraryCreated}
          onCancel={createOpenActions.setFalse}
        />
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default ResourceLibrariesList;
